import { Card, CardContent } from "../../../../components/ui/card"
import { Quote } from "lucide-react"
import "../../../../styles/shiny-button.css"
import "../../../../styles/learn-more-button.css"

const testimonials = [
  {
    name: "Amina Al-Hassan",
    location: "Cairo, Egypt",
    course: "Quran Reading & Tajweed",
    rating: 5,
    text: "Within a few months my tajweed improved dramatically. The ustadha keeps us focused on the Qur'an while reminding us of the adab of recitation.",
  },
  {
    name: "Abdullah Khan",
    location: "London, United Kingdom",
    course: "Arabic Language",
    rating: 5,
    text: "The Arabic lessons balance classical texts with modern conversation. Our teacher always links vocabulary to Qur'anic verses and prophetic narrations.",
  },
  {
    name: "Fatimah Siddiqui",
    location: "Toronto, Canada",
    course: "Islamic Studies",
    rating: 5,
    text: "As a revert, I needed a structured path. The lessons on aqeedah and seerah are clear, compassionate, and rooted in authentic sources.",
  },
  {
    name: "Yusuf Al-Mutairi",
    location: "Dubai, UAE",
    course: "Quran Memorization",
    rating: 5,
    text: "The hifdh program is inspiring. My son now leads us in salah with confidence and continues his memorisation with love for the Qur'an.",
  },
  {
    name: "Zainab Rahman",
    location: "Kuala Lumpur, Malaysia",
    course: "Quran & Arabic",
    rating: 5,
    text: "Combining Arabic grammar with Qur'an study helped me ponder the meanings during taraweeh. The schedule respects family life and ibadah.",
  },
  {
    name: "Iman Ahmed",
    location: "Johannesburg, South Africa",
    course: "Advanced Arabic",
    rating: 5,
    text: "Our instructor connects us to classical scholars and contemporary usage. It is rare to find such depth with genuine Islamic ethos online.",
  },
]

export function TestimonialsSection() {
  return (
    <section id="testimonials" className="py-16 sm:py-20 lg:py-24">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="mx-auto max-w-3xl text-balance text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">
            What Our <span className="text-transparent bg-gradient-to-r from-[#245E51] to-[#A8FF51] bg-clip-text">Students</span> Say
          </h2>
          <p className="mt-4 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto text-pretty">
            Hear from our global community of students who have transformed their Quranic and Arabic learning journey with us.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid gap-8 lg:gap-12">
            {/* First row - 2 cards */}
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              {testimonials.slice(0, 2).map((t, index) => (
                <Card
                  key={index}
                  className={`group relative overflow-hidden rounded-3xl border border-white/50 bg-white/80 backdrop-blur-sm transition-all duration-500 hover:bg-white/90 hover:border-white/70 hover:scale-[1.02] ${index === 0 ? 'rotate-1 hover:rotate-0' : '-rotate-1 hover:rotate-0'}`}
                >
                  <CardContent className="p-8">
                    <div className="mb-6">
                      <h4 className="font-bold text-gray-900 text-lg">{t.name}</h4>
                      <p className="text-sm text-gray-500 mb-2">{t.location}</p>
                      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${
                        t.course === 'Quran Reading & Tajweed' ? 'bg-orange-100' :
                        t.course === 'Arabic Language' ? 'bg-yellow-100' :
                        t.course === 'Islamic Studies' ? 'bg-orange-100' :
                        t.course === 'Quran Memorization' ? 'bg-yellow-100' :
                        t.course === 'Advanced Arabic' ? 'bg-[#A8FF51]/20' :
                        t.course === 'Quran & Arabic' ? 'bg-indigo-100' :
                        'bg-[#A8FF51]/20'
                      }`}>
                        <div className={`h-2 w-2 rounded-full ${
                          t.course === 'Quran Reading & Tajweed' ? 'bg-orange-400' :
                          t.course === 'Arabic Language' ? 'bg-yellow-400' :
                          t.course === 'Islamic Studies' ? 'bg-orange-400' :
                          t.course === 'Quran Memorization' ? 'bg-yellow-400' :
                          t.course === 'Advanced Arabic' ? 'bg-[#A8FF51]' :
                          t.course === 'Quran & Arabic' ? 'bg-indigo-400' :
                          'bg-[#A8FF51]'
                        }`}></div>
                        <span className={`text-xs font-medium ${
                          t.course === 'Quran Reading & Tajweed' ? 'text-orange-700' :
                          t.course === 'Arabic Language' ? 'text-yellow-700' :
                          t.course === 'Islamic Studies' ? 'text-orange-700' :
                          t.course === 'Quran Memorization' ? 'text-yellow-700' :
                          t.course === 'Advanced Arabic' ? 'text-[#245E51]' :
                          t.course === 'Quran & Arabic' ? 'text-indigo-700' :
                          'text-[#245E51]'
                        }`}>{t.course}</span>
                      </div>
                    </div>
                    
                    <div className="relative">
                      <Quote className="absolute -top-2 -left-2 h-8 w-8 text-amber-600/20" />
                      <p className="text-lg leading-8 text-gray-700 pl-6 italic">"{t.text}"</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Second row - 3 cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {testimonials.slice(2, 5).map((t, index) => (
                <Card
                  key={index + 2}
                  className={`group relative overflow-hidden rounded-3xl border border-white/50 bg-white/80 backdrop-blur-sm transition-all duration-500 hover:bg-white/90 hover:border-white/70 hover:scale-[1.02] ${index === 0 ? 'rotate-2 hover:rotate-0' : index === 1 ? '-rotate-1 hover:rotate-0' : 'rotate-1 hover:rotate-0'}`}
                >
                  <CardContent className="p-6">
                    <div className="text-center mb-6">
                      <h4 className="font-bold text-gray-900">{t.name}</h4>
                      <p className="text-sm text-gray-500">{t.location}</p>
                    </div>
                    
                    <div className="relative mb-6">
                      <Quote className="absolute -top-1 -left-1 h-6 w-6 text-amber-600/30" />
                      <p className="text-sm leading-6 text-gray-700 pl-4">"{t.text}"</p>
                    </div>
                    
                    <div className="text-center">
                      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${
                        t.course === 'Quran Reading & Tajweed' ? 'bg-orange-100' :
                        t.course === 'Arabic Language' ? 'bg-blue-100' :
                        t.course === 'Islamic Studies' ? 'bg-purple-100' :
                        t.course === 'Quran Memorization' ? 'bg-yellow-100' :
                        t.course === 'Advanced Arabic' ? 'bg-[#A8FF51]/20' :
                        t.course === 'Quran & Arabic' ? 'bg-indigo-100' :
                        'bg-teal-100'
                      }`}>
                        <div className={`h-1.5 w-1.5 rounded-full ${
                          t.course === 'Quran Reading & Tajweed' ? 'bg-orange-400' :
                          t.course === 'Arabic Language' ? 'bg-blue-400' :
                          t.course === 'Islamic Studies' ? 'bg-purple-400' :
                          t.course === 'Quran Memorization' ? 'bg-yellow-400' :
                          t.course === 'Advanced Arabic' ? 'bg-[#A8FF51]' :
                          t.course === 'Quran & Arabic' ? 'bg-indigo-400' :
                          'bg-teal-400'
                        }`}></div>
                        <span className={`text-xs font-medium ${
                          t.course === 'Quran Reading & Tajweed' ? 'text-orange-700' :
                          t.course === 'Arabic Language' ? 'text-blue-700' :
                          t.course === 'Islamic Studies' ? 'text-purple-700' :
                          t.course === 'Quran Memorization' ? 'text-yellow-700' :
                          t.course === 'Advanced Arabic' ? 'text-[#245E51]' :
                          t.course === 'Quran & Arabic' ? 'text-indigo-700' :
                          'text-teal-700'
                        }`}>{t.course}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Third row - 1 featured card */}
            {testimonials.slice(5, 6).map((t, index) => (
              <div key={index + 5} className="flex justify-center">
                <Card className="group relative overflow-hidden rounded-3xl border border-white/50 bg-white/80 backdrop-blur-sm transition-all duration-500 hover:bg-white/90 hover:border-white/70 hover:scale-[1.02] max-w-2xl w-full -rotate-2 hover:rotate-0">
                  <CardContent className="p-8">
                    <div className="mb-6">
                      <h4 className="font-bold text-gray-900 text-xl mb-1">{t.name}</h4>
                      <p className="text-gray-500 mb-3">{t.location}</p>
                      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                        t.course === 'Quran Reading & Tajweed' ? 'bg-orange-100' :
                        t.course === 'Arabic Language' ? 'bg-blue-100' :
                        t.course === 'Islamic Studies' ? 'bg-purple-100' :
                        t.course === 'Quran Memorization' ? 'bg-yellow-100' :
                        t.course === 'Advanced Arabic' ? 'bg-[#A8FF51]/20' :
                        t.course === 'Quran & Arabic' ? 'bg-indigo-100' :
                        'bg-teal-100'
                      }`}>
                        <div className={`h-2.5 w-2.5 rounded-full ${
                          t.course === 'Quran Reading & Tajweed' ? 'bg-orange-400' :
                          t.course === 'Arabic Language' ? 'bg-blue-400' :
                          t.course === 'Islamic Studies' ? 'bg-purple-400' :
                          t.course === 'Quran Memorization' ? 'bg-yellow-400' :
                          t.course === 'Advanced Arabic' ? 'bg-[#A8FF51]' :
                          t.course === 'Quran & Arabic' ? 'bg-indigo-400' :
                          'bg-teal-400'
                        }`}></div>
                        <span className={`text-sm font-medium ${
                          t.course === 'Quran Reading & Tajweed' ? 'text-orange-700' :
                          t.course === 'Arabic Language' ? 'text-blue-700' :
                          t.course === 'Islamic Studies' ? 'text-purple-700' :
                          t.course === 'Quran Memorization' ? 'text-yellow-700' :
                          t.course === 'Advanced Arabic' ? 'text-[#245E51]' :
                          t.course === 'Quran & Arabic' ? 'text-indigo-700' :
                          'text-teal-700'
                        }`}>{t.course}</span>
                      </div>
                    </div>
                    
                    <div className="relative">
                      <Quote className="absolute -top-3 -left-3 h-10 w-10 text-amber-600/20" />
                      <p className="text-xl leading-9 text-gray-700 pl-8 italic">"{t.text}"</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
        

        <div className="text-center mt-16">
          <a href="/reviews" className="learn-more">View All Reviews</a>
        </div>
      </div>
    </section>
  )
}
